import React, { useEffect, useState } from "react";
import {
  Image,
  Text,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { AppColors } from "../../utility/AppColors";
import { AppFontStyle } from "../../styles/AppFontStyle";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import Separator from "../Separator";
import { AppImages } from "../../utility/AppImages";
import { CommonButton } from "../../common/CommonButton";
import ProfileTooltip from "./ProfileTooltip";

import { createStripeAccount } from "../../services/authAPI";
import { toast } from "react-toastify";
import * as Linking from "expo-linking";
import { ShowLoader } from "../../common/ShowLoader";
import { useAuthProvider } from "../../context/AuthContext";
import UserCreditsSkeleton from "../skeleton-components/UserCreditsSkeleton";
import BalanceWalkthrough from "../walk-through-common-components/profileTooltips/BalanceWalkthrough";

interface UserProfileData {
  balance?: number;
  credits?: number;
  stripeCustomerId?: string;
  dailyCashoutAmount?: number;
}

interface UserCreditsProps {
  userProfileData?: UserProfileData;
  setRedeemBalance: (value: boolean) => void;
  totalBalanceShowInfo: boolean;
  setTotalBalanceShowInfo: (value: boolean) => void;
  availableCreditsShowInfo: boolean;
  setAvailableCreditsShowInfo: (value: boolean) => void;
  accessToken?: string;
  isLoading?: boolean;
  scrollViewRef?: any;
  setActiveTab?: any;
  totalShowInfo?: boolean;
  coinsShowInfo?: boolean;
}

const UserCredits: React.FC<UserCreditsProps> = ({
  userProfileData,
  setRedeemBalance,
  totalBalanceShowInfo,
  setTotalBalanceShowInfo,
  availableCreditsShowInfo,
  setAvailableCreditsShowInfo,
  accessToken,
  isLoading = false,
  setActiveTab,
  scrollViewRef,
  totalShowInfo = false,
  coinsShowInfo = false,
}) => {
  const deviceSize = useDeviceSize();
  const styles = useStyles();
  const [showLoader, setShowLoader] = useState(false);

  const { isActiveSubscriptionFound } = useAuthProvider();

  const createStripeCustomer = async () => {
    setShowLoader(true);
    try {
      const data = await createStripeAccount(accessToken);
      if (data.statusCode === 303) {
        Linking.openURL(data.body.url);
      } else {
        toast.error(data.error || "An error occurred during creating account");
      }
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setShowLoader(false);
    }
  };

  const getFormattedBalance = () => {
    if (!userProfileData || userProfileData.balance === undefined) return "-";
    const balance = Number(userProfileData.balance);
    return isNaN(balance) ? "-" : balance.toFixed(2);
  };

  if (isLoading) {
    return <UserCreditsSkeleton />;
  }

  return (
    <View
      style={{
        gap:
          deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
            ? 12
            : 30,
        flex:
          deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
            ? 1
            : 0.5,
        width:
          deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
            ? "99%"
            : "auto",
        margin:
          deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
            ? 1
            : 0,
      }}
    >
      <BalanceWalkthrough
        setActiveTab={setActiveTab}
        scrollViewRef={scrollViewRef}
        content={
          <LinearGradient
            colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              padding: 24,
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 24,
              alignSelf: "stretch",
              borderRadius: 8,
              borderWidth: 0.8,
              borderColor: AppColors.gray_374151,
              ...(totalShowInfo || coinsShowInfo || availableCreditsShowInfo
                ? { zIndex: -1 }
                : {}),
            }}
          >
            {/* Total Balance Section */}
            <View style={{ alignSelf: "stretch" }}>
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  alignSelf: "stretch",
                }}
              >
                <Text
                  style={[
                    deviceSize === DEVICE_SIZES.XS ||
                    deviceSize === DEVICE_SIZES.SM
                      ? AppFontStyle.BOLD_ANYBODY_18
                      : AppFontStyle.BOLD_ANYBODY_24,
                    { lineHeight: 27.72 },
                  ]}
                >
                  Total Balance
                </Text>
                <TouchableOpacity
                  onPress={() => setTotalBalanceShowInfo(!totalBalanceShowInfo)}
                  style={{ flexDirection: "row", alignItems: "center", gap: 4 }}
                >
                  <Text
                    style={[
                      AppFontStyle.BOLD_ANYBODY_16,
                      {
                        color: AppColors.koBrandRed,
                        lineHeight: 18.48,
                        marginRight: 4,
                      },
                    ]}
                  >
                    What are credits?
                  </Text>
                  <Image
                    source={AppImages.icInfoCircle}
                    style={{ height: 20, width: 20, resizeMode: "contain" }}
                  />
                </TouchableOpacity>
              </View>

              <Separator
                color={AppColors.gray_374151}
                height={1}
                marginTop={12}
                marginBottom={24}
                marginHorizontal={0}
              />

              <View
                style={{
                  flexDirection: "row",
                  gap: 12,
                  alignItems: "center",
                }}
              >
                <Text
                  style={
                    deviceSize === DEVICE_SIZES.XS ||
                    deviceSize === DEVICE_SIZES.SM
                      ? AppFontStyle.SEMIBOLD_ANYBODY_28
                      : AppFontStyle.SEMIBOLD_ANYBODY_41
                  }
                >
                  £
                </Text>
                <Text
                  style={
                    deviceSize === DEVICE_SIZES.XS ||
                    deviceSize === DEVICE_SIZES.SM
                      ? AppFontStyle.REGULAR_ANYBODY_22
                      : AppFontStyle.REGULAR_BEBAS_NEUE_35
                  }
                >
                  {getFormattedBalance()}
                </Text>
              </View>

              <View
                style={{
                  flexDirection: "row",
                  gap: 12,
                  alignItems: "center",
                  marginTop: 12,
                }}
              >
                <Text
                  style={
                    deviceSize === DEVICE_SIZES.XS ||
                    deviceSize === DEVICE_SIZES.SM
                      ? AppFontStyle.SEMIBOLD_ANYBODY_12
                      : AppFontStyle.SEMIBOLD_ANYBODY_14
                  }
                >
                  Daily withdrawal limit (Remain/Total)
                </Text>
                <Text
                  style={
                    deviceSize === DEVICE_SIZES.XS ||
                    deviceSize === DEVICE_SIZES.SM
                      ? AppFontStyle.SEMIBOLD_ANYBODY_12
                      : AppFontStyle.SEMIBOLD_ANYBODY_14
                  }
                >
                  £{(userProfileData?.dailyCashoutAmount || 0).toFixed(2)} / £
                  {isActiveSubscriptionFound ? 1000 : 400}
                </Text>
              </View>
              {totalBalanceShowInfo && (
                <TouchableWithoutFeedback onPress={(e) => e.stopPropagation()}>
                  <View
                    style={[
                      styles.infoPopupContainer,
                      {
                        right:
                          deviceSize === DEVICE_SIZES.XS ||
                          deviceSize === DEVICE_SIZES.SM
                            ? -20
                            : 30,
                      },
                    ]}
                  >
                    <ProfileTooltip
                      content={
                        <View>
                          <Text style={AppFontStyle.MEDIUM_ANYBODY_16}>
                            What Are Credits?
                          </Text>
                          <Text
                            style={[
                              AppFontStyle.REGULAR_ANYBODY_14,
                              { marginTop: 8 },
                            ]}
                          >
                            💳 KO Credits
                          </Text>
                          <Text
                            style={[
                              AppFontStyle.REGULAR_ANYBODY_14,
                              { marginTop: 4 },
                            ]}
                          >
                            KO Credits are what you earn by receiving gifts from
                            your audience or winning challenges and converting
                            belts.
                          </Text>
                          <Text
                            style={[
                              AppFontStyle.REGULAR_ANYBODY_14,
                              { marginTop: 12 },
                            ]}
                          >
                            They represent your effort turned into real value –
                            your way to get paid for your performance on Kall
                            Out KO.
                          </Text>
                          <Text
                            style={[
                              AppFontStyle.REGULAR_ANYBODY_14,
                              { marginTop: 12 },
                            ]}
                          >
                            Credits can't be bought; they're earned through
                            skill, engagement, and support – and can be redeemed
                            for real pay-outs based on your subscription tier.
                          </Text>
                          <Text
                            style={[
                              AppFontStyle.MEDIUM_ANYBODY_16,
                              { marginTop: 16 },
                            ]}
                          >
                            💷 Value:
                          </Text>
                          <Text
                            style={[
                              AppFontStyle.REGULAR_ANYBODY_14,
                              { marginTop: 8 },
                            ]}
                          >
                            1 KO credit = £0.010
                          </Text>
                          <Text
                            style={[
                              AppFontStyle.REGULAR_ANYBODY_14,
                              { marginTop: 12 },
                            ]}
                          >
                            In short:
                          </Text>
                          <Text
                            style={[
                              AppFontStyle.REGULAR_ANYBODY_14,
                              { marginTop: 4 },
                            ]}
                          >
                            Credits are your effort rewarded.
                          </Text>
                          <Text
                            style={[
                              AppFontStyle.REGULAR_ANYBODY_14,
                              { marginTop: 4 },
                            ]}
                          >
                            Earn them. Redeem them. Cash them out.
                          </Text>
                        </View>
                      }
                      bottom={
                        deviceSize === DEVICE_SIZES.XS ||
                        deviceSize === DEVICE_SIZES.SM
                          ? -50
                          : -50
                      }
                      right={
                        deviceSize === DEVICE_SIZES.XS ||
                        deviceSize === DEVICE_SIZES.SM
                          ? -10
                          : 20
                      }
                      width={
                        deviceSize === DEVICE_SIZES.XS ||
                        deviceSize === DEVICE_SIZES.SM
                          ? 300
                          : 350
                      }
                      visible={totalBalanceShowInfo}
                      setVisible={setTotalBalanceShowInfo}
                    />
                  </View>
                </TouchableWithoutFeedback>
              )}
            </View>

            {/* Stripe Section */}
            <LinearGradient
              colors={["rgba(99, 91, 255, 0.30)", "rgba(28, 57, 142, 0.30)"]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={{
                minHeight: 87,
                paddingHorizontal: 16,
                paddingVertical: 16,
                flexDirection:
                  deviceSize === DEVICE_SIZES.XS ||
                  deviceSize === DEVICE_SIZES.SM
                    ? userProfileData?.stripeCustomerId
                      ? "row"
                      : "column"
                    : "row",
                justifyContent: "space-between",
                alignItems: "center",
                alignSelf: "stretch",
                borderRadius: 10,
                borderWidth: 0.8,
                borderColor: "rgba(220, 38, 38, 0.50)",
                gap:
                  deviceSize === DEVICE_SIZES.XS ||
                  deviceSize === DEVICE_SIZES.SM
                    ? 12
                    : 0,
              }}
            >
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <Image
                  source={AppImages.Ko_Stripe}
                  style={{
                    width: 42,
                    height: 41,
                    resizeMode: "contain",
                  }}
                />
                <View style={{ flexDirection: "column", gap: 4 }}>
                  <Text
                    style={[
                      AppFontStyle.MEDIUM_ANYBODY_16,
                      { lineHeight: 18.48 },
                    ]}
                  >
                    Stripe
                  </Text>
                  {userProfileData?.stripeCustomerId ? (
                    <Text
                      style={[
                        AppFontStyle.REGULAR_ANYBODY_14,
                        { color: AppColors.gray_9CA3AF, lineHeight: 16.17 },
                      ]}
                    >
                      Ready for payouts
                    </Text>
                  ) : (
                    <Text
                      style={[
                        AppFontStyle.REGULAR_ANYBODY_14,
                        { color: AppColors.koBrandRed, lineHeight: 16.17 },
                      ]}
                    >
                      Stripe Disconnect
                    </Text>
                  )}
                </View>
              </View>
              {userProfileData?.stripeCustomerId ? (
                <Image
                  source={AppImages.CheckInTick}
                  style={{ height: 24, width: 24, resizeMode: "contain" }}
                />
              ) : (
                <CommonButton
                  buttonStyle={[
                    styles.buyBeltButtonStyle,
                    deviceSize === DEVICE_SIZES.XS ||
                    deviceSize === DEVICE_SIZES.SM
                      ? { width: "100%" }
                      : {},
                  ]}
                  textStyle={[AppFontStyle.BOLD_ANYBODY_12, {}]}
                  title="Create Stripe Account"
                  onTap={createStripeCustomer}
                />
              )}
            </LinearGradient>
          </LinearGradient>
        }
      />

      {/* Available Credits Section */}
      <LinearGradient
        colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{
          padding: 24,
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 12,
          alignSelf: "stretch",
          borderRadius: 8,
          borderWidth: 0.8,
          borderColor: AppColors.gray_374151,
          ...(totalShowInfo || coinsShowInfo || totalBalanceShowInfo
            ? { zIndex: -1 }
            : {}),
        }}
      >
        <LinearGradient
          colors={["rgba(0, 79, 59, 0.20)", "rgba(11, 79, 74, 0.20)"]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{
            padding: 16.8,
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            gap: 12,
            alignSelf: "stretch",
            borderRadius: 10,
            borderWidth: 0.8,
            borderColor: "rgba(0, 188, 125, 0.30)",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              alignSelf: "stretch",
            }}
          >
            <Text
              style={[AppFontStyle.MEDIUM_ANYBODY_16, { lineHeight: 18.48 }]}
            >
              Available Credits to redeem
            </Text>
          </View>
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              alignSelf: "stretch",
            }}
          >
            <Text
              style={
                deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
                  ? AppFontStyle.REGULAR_ANYBODY_22
                  : AppFontStyle.REGULAR_BEBAS_NEUE_35
              }
            >
              {userProfileData?.credits ? userProfileData?.credits : 0}
            </Text>
            <LinearGradient
              colors={
                userProfileData?.stripeCustomerId
                  ? ["#DC2626", "#991B1B"]
                  : ["rgba(156, 163, 175, 0.5)", "rgba(107, 114, 128, 0.5)"]
              }
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={{
                borderRadius: 6,
              }}
            >
              <CommonButton
                buttonStyle={[styles.redeemButtonStyle]}
                textStyle={[
                  AppFontStyle.BOLD_ANYBODY_12,
                  !userProfileData?.stripeCustomerId && {
                    color: AppColors.gray_9CA3AF,
                  },
                ]}
                title="Redeem"
                disabled={!userProfileData?.stripeCustomerId}
                onTap={() => {
                  if (userProfileData?.stripeCustomerId) {
                    setRedeemBalance(true);
                  }
                }}
              />
            </LinearGradient>
          </View>
        </LinearGradient>
      </LinearGradient>
    </View>
  );
};

export default UserCredits;

const useStyles = CreateResponsiveStyle(
  {
    buyBeltButtonStyle: {
      paddingVertical: 12,
      shadowOpacity: 0,
      paddingHorizontal: 16,
      borderRadius: 10,
      borderWidth: 1,
      backgroundColor: AppColors.appViolentColor_6D08C3,
      borderColor: AppColors.appViolentColor_6D08C3,
    },
    redeemButtonStyle: {
      paddingVertical: 10,
      shadowOpacity: 0,
      paddingHorizontal: 24,
      borderRadius: 6,
      width: 101,
      height: 42,
      backgroundColor: "transparent",
      borderWidth: 0,
    },
    infoPopupContainer: {
      position: "absolute",
      top: -10,
      zIndex: 9999,
    },
  },
  {
    [DEVICE_SIZES.MD]: {},
    [DEVICE_SIZES.LG]: {},
    [DEVICE_SIZES.SM]: {},
    [DEVICE_SIZES.XS]: {},
  }
);
